import { useState } from "react";
import axios from "axios";
import { Send, CheckCircle2, AlertTriangle } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import Reveal from "../components/Reveal";
import { SITE_NAME } from "../config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBJECTS = ["Question générale", "Signaler une erreur", "Suggestion", "Autre"];

const initial = { name: "", email: "", subject: SUBJECTS[0], message: "", rgpd: false, website: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [error, setError] = useState("");

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      setForm(initial);
    } catch (err) {
      setStatus("error");
      setError(
        err.response?.data?.detail ||
          "Une erreur est survenue. Vérifiez les champs et réessayez.",
      );
    }
  };

  const inputCls =
    "w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-base transition-colors focus:border-[#2BB08A]";

  return (
    <>
      <Seo
        title={`Contact – ${SITE_NAME}`}
        description="Contactez la rédaction pour signaler une erreur, poser une question ou proposer une correction."
        path="/contact"
      />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 sm:py-12">
        <div className="max-w-[760px]">
          <Breadcrumbs items={[{ label: "Le site" }, { label: "Nous contacter" }]} />
          <Reveal>
            <h1 className="font-display text-3xl font-bold tracking-tight text-[#0B1F3A] sm:text-4xl">
              Nous contacter
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-slate-600">
              Une erreur, une question, une mise à jour à signaler ? Écrivez-nous. Nous ne
              répondons pas aux demandes de pronostics ni de conseils de paris.
            </p>
          </Reveal>

          {status === "success" ? (
            <div
              data-testid="contact-success"
              role="status"
              className="mt-8 rounded-xl border-l-4 border-[#2BB08A] bg-[#E8F8F3] p-6 text-[#064E3B]"
            >
              <p className="flex items-center gap-2 font-display text-base font-semibold">
                <CheckCircle2 className="h-5 w-5" aria-hidden="true" />
                Message envoyé
              </p>
              <p className="mt-2 text-[15px]">
                Merci, votre message a bien été envoyé. Nous vous répondrons dans les meilleurs
                délais.
              </p>
              <button
                type="button"
                data-testid="contact-send-another"
                onClick={() => setStatus("idle")}
                className="mt-4 rounded-full border border-[#2BB08A] px-5 py-2 text-sm font-semibold transition-colors hover:bg-white"
              >
                Envoyer un autre message
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="contact-form" noValidate={false}>
              {/* Honeypot anti-spam : invisible pour les humains */}
              <div className="absolute -left-[9999px] h-0 w-0 overflow-hidden" aria-hidden="true">
                <label htmlFor="contact-website">Ne pas remplir</label>
                <input
                  id="contact-website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={form.website}
                  onChange={set("website")}
                />
              </div>

              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-name" className="mb-1.5 block text-sm font-medium text-slate-700">
                    Nom <span aria-hidden="true" className="text-[#E8A317]">*</span>
                  </label>
                  <input
                    id="contact-name"
                    data-testid="contact-input-name"
                    type="text"
                    required
                    minLength={2}
                    value={form.name}
                    onChange={set("name")}
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-1.5 block text-sm font-medium text-slate-700">
                    E-mail <span aria-hidden="true" className="text-[#E8A317]">*</span>
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-input-email"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className={inputCls}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="contact-subject" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Objet <span aria-hidden="true" className="text-[#E8A317]">*</span>
                </label>
                <select
                  id="contact-subject"
                  data-testid="contact-input-subject"
                  value={form.subject}
                  onChange={set("subject")}
                  className={inputCls}
                >
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="contact-message" className="mb-1.5 block text-sm font-medium text-slate-700">
                  Message <span aria-hidden="true" className="text-[#E8A317]">*</span>
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  required
                  minLength={10}
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  className={inputCls}
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  id="contact-rgpd"
                  data-testid="contact-input-rgpd"
                  type="checkbox"
                  required
                  checked={form.rgpd}
                  onChange={set("rgpd")}
                  className="mt-1 h-4 w-4 rounded border-slate-300 accent-[#2BB08A]"
                />
                <label htmlFor="contact-rgpd" className="text-sm leading-relaxed text-slate-600">
                  J'accepte que mes données soient utilisées pour répondre à ma demande.{" "}
                  <span aria-hidden="true" className="text-[#E8A317]">*</span>
                </label>
              </div>

              {status === "error" && (
                <p
                  data-testid="contact-error"
                  role="alert"
                  className="flex items-start gap-2 rounded-lg border-l-4 border-[#E8A317] bg-[#FEF3C7] p-4 text-sm font-medium text-[#78350F]"
                >
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
                  {error}
                </p>
              )}

              <button
                type="submit"
                data-testid="contact-submit"
                disabled={status === "sending"}
                className="inline-flex items-center gap-2 rounded-full bg-[#0B1F3A] px-7 py-3 font-display text-sm font-semibold text-white transition-colors hover:bg-[#16305a] disabled:opacity-50"
              >
                <Send className="h-4 w-4" aria-hidden="true" />
                {status === "sending" ? "Envoi en cours..." : "Envoyer"}
              </button>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
